/**
 * landing/search 검색 자동완성 island.
 *
 * Mount: `<input data-mount="search" />` + `<div data-search-results hidden>`
 *        + `<script id="catalog" type="application/json">` (빌드 시 인라인).
 *
 * 흐름:
 *   - 입력 → 150ms debounce → 인라인 catalog 부분 일치 (서비스명·brand·title·slug)
 *   - 결과 ≤ 5 행 노출, 0 건일 땐 [request/_page] 요청 폼 링크
 *   - 결과 행 클릭 또는 Enter → /g/<svc>/<guide>/ 로 이동
 *   - ⌘K (Mac) / Ctrl+K (Win) → input focus
 *
 * fetch 0 — 모든 검색은 빌드 시 인라인된 JSON 안에서.
 */

type CatalogEntry = {
  service_slug: string;
  guide_slug: string;
  title: string;
  brand?: string;
  service_name?: string;
  keywords?: string[];
};

const DEBOUNCE_MS = 150;
const MAX_RESULTS = 5;

function readCatalog(): CatalogEntry[] {
  const tag = document.querySelector<HTMLScriptElement>('script[type="application/json"]#catalog');
  if (!tag) return [];
  try {
    const raw = JSON.parse(tag.textContent ?? "[]");
    return Array.isArray(raw) ? (raw as CatalogEntry[]) : [];
  } catch {
    return [];
  }
}

function normalize(s: string): string {
  return s.toLowerCase().trim();
}

function matches(entry: CatalogEntry, q: string): boolean {
  const needle = normalize(q);
  if (needle.length === 0) return false;
  const haystack = [
    entry.title,
    entry.brand ?? "",
    entry.service_name ?? "",
    entry.service_slug,
    entry.guide_slug,
    ...(entry.keywords ?? []),
  ]
    .map(normalize)
    .join("\n");
  return haystack.includes(needle);
}

function rankResults(catalog: CatalogEntry[], q: string): CatalogEntry[] {
  return catalog.filter((e) => matches(e, q)).slice(0, MAX_RESULTS);
}

function renderResults(container: HTMLElement, hits: CatalogEntry[], q: string): void {
  container.innerHTML = "";
  if (q.length === 0) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  if (hits.length === 0) {
    const empty = document.createElement("div");
    empty.dataset.searchEmpty = "";
    empty.className = "rounded-md border border-slate-200 bg-white p-4 text-sm";
    empty.innerHTML = `
      <p class="text-slate-700">"${escapeHtml(q)}" 가이드가 아직 없어요.</p>
      <a href="/request/" class="mt-2 inline-block text-sm font-medium text-rose-600 hover:underline">이 서비스 가이드 요청하기 →</a>
    `;
    container.appendChild(empty);
    return;
  }
  const list = document.createElement("ul");
  list.dataset.searchList = "";
  list.className = "divide-y divide-slate-100 rounded-md border border-slate-200 bg-white";
  for (const [idx, hit] of hits.entries()) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = `/g/${encodeURIComponent(hit.service_slug)}/${encodeURIComponent(hit.guide_slug)}/`;
    a.dataset.searchHit = "";
    a.dataset.searchIndex = String(idx);
    a.className = "block px-4 py-3 text-sm hover:bg-slate-50 focus:bg-slate-100 focus:outline-none";
    const label = document.createElement("p");
    label.className = "font-medium text-slate-900";
    label.textContent = `${hit.service_name ?? hit.brand ?? hit.service_slug} — ${hit.title}`;
    a.appendChild(label);
    li.appendChild(a);
    list.appendChild(li);
  }
  container.appendChild(list);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function mountSearch(input: HTMLInputElement): void {
  const root = input.closest<HTMLElement>("[data-search-root]") ?? input.parentElement;
  if (!root) return;
  const container = root.querySelector<HTMLElement>("[data-search-results]");
  if (!container) return;

  const catalog = readCatalog();
  let timer: ReturnType<typeof setTimeout> | null = null;

  function run(): void {
    const q = input.value;
    const hits = rankResults(catalog, q);
    renderResults(container as HTMLElement, hits, q);
  }

  input.addEventListener("input", () => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      const first = container?.querySelector<HTMLAnchorElement>("[data-search-hit]");
      if (first) {
        e.preventDefault();
        first.click();
      }
    }
  });

  document.addEventListener("keydown", (e) => {
    const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
    if (isCmdK) {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });

  // bfcache 복귀 (가이드 페이지에서 뒤로가기) 시 input + dropdown 초기화.
  // bfcache 가 페이지 전체 DOM 을 메모리에서 복원해 input.value 가 그대로
  // 남는 회귀 — `event.persisted=true` 로 그 케이스만 잡아 비우기.
  window.addEventListener("pageshow", (e) => {
    if ((e as PageTransitionEvent).persisted) {
      input.value = "";
      renderResults(container as HTMLElement, [], "");
    }
  });
}
